import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import styles from './index.module.scss'
import { Button, Modal } from 'antd'
import { League } from 'types/leagues'
import { createLeague, deleteLeague, editLeague, getLeagueById, getLeagues } from 'services/leagues'
import { LeaguesTable } from '../../components/leagues-table'
import { LeagueForm } from '../../components/league-form'
import { addTeamToLeague, deleteTeamFromLeague } from 'services/teams_to_leagues'
import { AddTeamToLeagueForm } from '../../components/add-team-to-league-form'
import { useNavigate } from 'react-router-dom'
import { modalTitle as formatModalTitle } from 'utils/modal-title'

export const Leagues = memo(() => {
    const [leagues, setLeagues] = useState<League[]>([])
    const [open, setOpen] = useState<boolean>(false)
    const [league, setLeague] = useState<League>({} as League)
    const [loading, setLoading] = useState<boolean>(false)
    const [dataIsFetching, setDataIsFetching] = useState<boolean>(false)

    const [openAddTeam, setOpenAddTeam] = useState<boolean>(false)
    const [selectedLeagueForAddTeam, setSelectedLeagueForAddTeam] = useState<number>()

    const navigate = useNavigate()

    const getAllData = useCallback(() => {
        setDataIsFetching(true)

        const getAll = async function () {
            return await getLeagues()
        }

        getAll()
            .then(data => {
                setLeagues(data)
            })
            .finally(() => {
                setDataIsFetching(false)
            })
    }, [])

    useEffect(() => {
        getAllData()
    }, [getAllData])

    const toggleModal = useCallback(() => {
        setOpen(!open)
    }, [open])

    const handleAdd = useCallback(() => {
        setLeague({} as League)
        toggleModal()
    }, [toggleModal])

    const handleEdit = useCallback(
        (id: number) => {
            setLoading(true)
            getLeagueById(id)
                .then(item => {
                    setLeague(item)
                    toggleModal()
                })
                .finally(() => {
                    setLoading(false)
                })
        },
        [toggleModal]
    )

    const handleDelete = useCallback(
        (id: number) => {
            setLoading(true)
            deleteLeague(id)
                .then(getAllData)
                .finally(() => {
                    setLoading(false)
                })
        },
        [getAllData]
    )

    const handleFormSubmit = useCallback(
        async (submitValues: League) => {
            setLoading(true)

            try {
                if (league.id) {
                    await editLeague({
                        ...submitValues,
                        id: league.id,
                    })
                } else {
                    await createLeague(submitValues)
                }
                await getAllData()
                toggleModal()
            } finally {
                setLoading(false)
            }
        },
        [getAllData, league, toggleModal]
    )

    const toggleAddTeamModal = useCallback(() => {
        setOpenAddTeam(!openAddTeam)
    }, [openAddTeam])

    const showAddTeamModal = useCallback(
        (leagueId: number) => {
            setSelectedLeagueForAddTeam(leagueId)
            toggleAddTeamModal()
        },
        [toggleAddTeamModal]
    )

    const handleAddTeam = useCallback(
        (teamId: number, leagueId: number) => {
            addTeamToLeague(teamId, leagueId).then(() => {
                getAllData()
                toggleAddTeamModal()
            })
        },
        [getAllData, toggleAddTeamModal]
    )

    const handleDeleteTeam = useCallback(
        (teamId: number, leagueId: number) => {
            deleteTeamFromLeague(teamId, leagueId).then(getAllData)
        },
        [getAllData]
    )

    const goToScoreTable = useCallback(
        (leagueId: number) => {
            navigate(`${leagueId}/score-table`)
        },
        [navigate]
    )

    const goToScorerTable = useCallback(
        (leagueId: number) => {
            navigate(`${leagueId}/scorer-table`)
        },
        [navigate]
    )

    const editMode = !!league.id
    const modalTitle = `${editMode ? `Edit league ${league.name}` : `Add new league`}`

    const somethingLoading = loading || dataIsFetching

    const selectedLeague = useMemo(() => {
        return leagues.find(it => it.id === selectedLeagueForAddTeam)
    }, [leagues, selectedLeagueForAddTeam])

    return (
        <div>
            Leagues
            <div className={styles.actions}>
                <Button type={'primary'} onClick={handleAdd}>
                    Add
                </Button>
            </div>
            <LeaguesTable
                data={leagues}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onAddTeam={showAddTeamModal}
                onDeleteTeam={handleDeleteTeam}
                loading={somethingLoading}
                onShowScoreTable={goToScoreTable}
                onShowScorerTable={goToScorerTable}
            />
            <Modal
                title={modalTitle}
                centered
                open={open}
                width={1000}
                destroyOnClose
                footer={null}
                onCancel={toggleModal}
                maskClosable={false}
            >
                <LeagueForm item={league} onSubmit={handleFormSubmit} loading={somethingLoading} />
            </Modal>
            <Modal
                title={formatModalTitle('Add team to league', selectedLeague?.name)}
                centered
                open={openAddTeam}
                destroyOnClose
                footer={null}
                maskClosable={false}
                onCancel={toggleAddTeamModal}
            >
                <AddTeamToLeagueForm league={selectedLeague!} onAdd={handleAddTeam} />
            </Modal>
        </div>
    )
})
